/**
 * Sales Order Detail Drawer
 * Tabbed interface with Overview, Orders, and Shipments tabs
 * Uses BaseDrawer for consistent sizing
 */

import { useState, useEffect, useRef, type MouseEvent } from 'react'
import { Capacitor } from '@capacitor/core'
import { Filesystem, Directory } from '@capacitor/filesystem'
import { Share } from '@capacitor/share'
import { useTranslation, useIsRTL, getLocalizedValue, useSelector } from '@ufc/core'
import {
  Badge,
  Button,
  cn,
  BaseDrawer,
  Download,
  useToast,
} from '@ufc/uikit'
import {
  FileText,
  CalendarDays,
  Package,
  Truck,
  RefreshCw,
  ShoppingCart,
  Layers,
  CheckCircle2,
  Archive,
  Edit3,
  Loader2,
  MapPin,
  XCircle,
  Receipt,
  Ban,
} from '@ufc/uikit'
import type { SalesOrder, SalesOrderStatus } from '../../domain/types'
import { calculateUtilization } from '../components/sales-orders-section/utils'
import { ordersApi, OrderCard, OrderPdfDetailDrawer, type Order } from '@ufc/domains/orders'
import { shipmentsApi, ShipmentCard, type Shipment } from '@ufc/domains/shipments'
import { invoiceApi, type Invoice } from '@ufc/domains/finance'
import { salesOrdersApi } from '../../infrastructure/api/sales-orders-api'
import { selectIsSuperAdmin } from '@ufc/domains/auth'

interface SalesOrderDetailDrawerProps {
  /** @deprecated Use salesOrder instead */
  contract?: SalesOrder | null
  salesOrder?: SalesOrder | null
  open: boolean
  onOpenChange: (open: boolean) => void
  onRenew?: (salesOrderId: string) => void
  onReorder?: (salesOrder: SalesOrder) => void
  onEdit?: () => void
  /** Callback when clicking an order to open its drawer */
  onViewOrders?: (orderId: string) => void
  /** Callback when clicking a shipment to open its drawer */
  onViewShipments?: (shipmentId: string) => void
}

const statusConfig: Record<SalesOrderStatus, { gradient: string; icon: typeof CheckCircle2; bg: string; text: string }> = {
  active: { gradient: 'from-emerald-500 to-emerald-600', icon: CheckCircle2, bg: 'bg-emerald-100 dark:bg-emerald-900/30', text: 'text-emerald-600 dark:text-emerald-400' },
  completed: { gradient: 'from-indigo-500 to-indigo-600', icon: Archive, bg: 'bg-indigo-100 dark:bg-indigo-900/30', text: 'text-indigo-600 dark:text-indigo-400' },
  draft: { gradient: 'from-slate-500 to-slate-600', icon: FileText, bg: 'bg-slate-100 dark:bg-slate-800/50', text: 'text-slate-700 dark:text-slate-300' },
  canceled: { gradient: 'from-red-500 to-red-600', icon: XCircle, bg: 'bg-red-100 dark:bg-red-900/30', text: 'text-red-600 dark:text-red-400' },
}

const CITY_LABELS: Record<string, { ar: string; ur: string }> = {
  riyadh: { ar: 'الرياض', ur: 'ریاض' },
  jeddah: { ar: 'جدة', ur: 'جدہ' },
  makkah: { ar: 'مكة', ur: 'مکہ' },
  mecca: { ar: 'مكة', ur: 'مکہ' },
  madinah: { ar: 'المدينة المنورة', ur: 'مدینہ منورہ' },
  medina: { ar: 'المدينة المنورة', ur: 'مدینہ منورہ' },
  dammam: { ar: 'الدمام', ur: 'دمام' },
  khobar: { ar: 'الخبر', ur: 'خبر' },
  alkhobar: { ar: 'الخبر', ur: 'الخبر' },
  jubail: { ar: 'الجبيل', ur: 'جبیل' },
}

function getLocalizedCityName(city: string | undefined, language: string): string | undefined {
  if (!city) return undefined

  const trimmedCity = city.trim()
  if (!trimmedCity) return undefined

  if (language.startsWith('ar')) {
    return CITY_LABELS[trimmedCity.toLowerCase()]?.ar ?? trimmedCity
  }

  if (language.startsWith('ur')) {
    return CITY_LABELS[trimmedCity.toLowerCase()]?.ur ?? trimmedCity
  }

  return trimmedCity
}

function getLocalizedSalesOrderLocation(
  deliveryAddress: string | undefined,
  cityName: string | undefined,
  language: string,
): string | undefined {
  const localizedAddress = getLocalizedCityName(deliveryAddress, language)
  if (localizedAddress) return localizedAddress

  return getLocalizedCityName(cityName, language)
}

function getLocalizedSalesOrderUnit(
  unit: string | undefined,
  t: ReturnType<typeof useTranslation>['t'],
  language: string,
): string {
  if (!unit) return ''

  const normalizedUnit = unit.trim().toLowerCase()

  if (normalizedUnit === 'bags' || normalizedUnit === 'bag') {
    if (language.startsWith('ar')) return 'أكياس'
    if (language.startsWith('ur')) return 'تھیلے'
    return t('products.bags', 'Bags')
  }

  if (normalizedUnit === 'tons' || normalizedUnit === 'ton' || normalizedUnit === 'bulk') {
    if (language.startsWith('ar')) return 'طن'
    if (language.startsWith('ur')) return 'ٹن'
    return t('products.tons', 'Tons')
  }

  return unit
}

function normalizeStatus(status?: string): SalesOrderStatus | undefined {
  if (status === 'cancelled') return 'canceled'
  return status as SalesOrderStatus | undefined
}

function canCancelLinkedOrder(order: Order): boolean {
  const sapOrderNumber = order.sapSalesOrderNumber?.trim()
  return order.status === 'pending' && !sapOrderNumber
}

function resolveStatusConfig(status?: string) {
  const normalized = normalizeStatus(status)
  if (normalized && statusConfig[normalized]) return statusConfig[normalized]
  return statusConfig.draft
}

const resolveSalesOrderLookupId = (salesOrder: SalesOrder) => (
  salesOrder.strapiDocumentId ?? salesOrder.id
)


interface SalesOrderPdfPreview {
  salesOrderId: string
  orderNumber: string
  fileName: string
  fileSize: string
  blob: Blob
  url?: string
}

type PdfPreviewMode = 'iframe' | 'pdfjs'
type ToastFn = ReturnType<typeof useToast>['toast']
type TranslateFn = ReturnType<typeof useTranslation>['t']

function isNativePlatform(): boolean {
  return typeof Capacitor?.isNativePlatform === 'function' ? Capacitor.isNativePlatform() : false
}

function getCapacitorPlatform(): string {
  if (typeof Capacitor?.getPlatform === 'function') return Capacitor.getPlatform()
  return 'web'
}

function getPdfPreviewMode(isNative: boolean): PdfPreviewMode {
  return isNative ? 'pdfjs' : 'iframe'
}

function shouldUsePdfPreview(isNative: boolean): boolean {
  return isNative
}

function formatPdfFileSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 KB'
  const kb = bytes / 1024
  if (kb < 1024) return `${Math.max(1, Math.round(kb))} KB`
  return `${(kb / 1024).toFixed(1)} MB`
}

function isBlobUrl(url?: string): url is string {
  return typeof url === 'string' && url.startsWith('blob:')
}

function revokePdfUrl(url?: string): void {
  if (!isBlobUrl(url)) return
  URL.revokeObjectURL(url)
}

function normalizeShareUri(uri: string): string {
  if (uri.startsWith('file://') || uri.startsWith('content://') || uri.startsWith('http')) return uri
  if (uri.startsWith('/')) return `file://${uri}`
  return uri
}

function isFileShareUri(uri: string): boolean {
  return uri.startsWith('file://')
}

function sanitizePdfFilename(filename: string): string {
  const sanitized = filename.replace(/[\\/:*?"<>|]+/g, '_').replace(/\s+/g, ' ').trim()
  if (!sanitized) return 'sales-order-orders.pdf'
  return sanitized.toLowerCase().endsWith('.pdf') ? sanitized : `${sanitized}.pdf`
}

function buildNativeShareOptions(params: {
  title: string
  text?: string
  uri: string
  platform: string
}): { title: string; text?: string; url?: string; files?: string[] } {
  const normalized = normalizeShareUri(params.uri)
  if (params.platform === 'android' && isFileShareUri(normalized)) {
    return { title: params.title, files: [normalized] }
  }
  return { title: params.title, text: params.text, url: normalized }
}

function readBlobAsBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      if (typeof reader.result !== 'string') {
        reject(new Error('Failed to read PDF'))
        return
      }
      const split = reader.result.split(',')
      resolve(split.length > 1 ? split[1] : reader.result)
    }
    reader.onerror = () => reject(reader.error ?? new Error('Failed to read PDF'))
    reader.readAsDataURL(blob)
  })
}

async function createShareablePdfFile(blob: Blob, filename: string): Promise<string> {
  const safeFilename = sanitizePdfFilename(filename)
  const base64 = await readBlobAsBase64(blob)
  const result = await Filesystem.writeFile({
    path: safeFilename,
    data: base64,
    directory: Directory.Cache,
    recursive: true,
  })
  return normalizeShareUri(result.uri)
}

function createSalesOrderPdfPreview(params: {
  salesOrderId: string
  orderNumber: string
  blob: Blob
  filename: string
  isNative: boolean
}): SalesOrderPdfPreview {
  return {
    salesOrderId: params.salesOrderId,
    orderNumber: params.orderNumber,
    fileName: sanitizePdfFilename(params.filename),
    fileSize: formatPdfFileSize(params.blob.size),
    blob: params.blob,
    url: params.isNative ? undefined : URL.createObjectURL(params.blob),
  }
}

function setPdfPreviewState(
  current: SalesOrderPdfPreview | null,
  next: SalesOrderPdfPreview | null,
  setPreview: (preview: SalesOrderPdfPreview | null) => void
): void {
  revokePdfUrl(current?.url)
  setPreview(next)
}

async function shareSalesOrderPdfPreview(params: {
  preview: SalesOrderPdfPreview | null
  t: TranslateFn
  toast: ToastFn
}): Promise<void> {
  const { preview, t, toast } = params
  if (!preview) return

  try {
    const platform = getCapacitorPlatform()
    const nativeShare = isNativePlatform()
    if (nativeShare) {
      const uri = await createShareablePdfFile(preview.blob, preview.fileName)
      await Share.share(buildNativeShareOptions({
        title: preview.fileName,
        text: preview.orderNumber,
        uri,
        platform,
      }))
      return
    }

    const file = new File([preview.blob], preview.fileName, { type: 'application/pdf' })
    if (typeof navigator.canShare === 'function' && !navigator.canShare({ files: [file] })) {
      throw new Error('Share not supported')
    }
    await navigator.share({ title: preview.fileName, files: [file] })
  } catch (error) {
    console.error('[SalesOrderDetailDrawer] Error sharing sales order PDF:', error)
    toast({
      title: t('common.error'),
      description: t('orders.pdfShareErrorDesc'),
      variant: 'destructive',
    })
  }
}

function getCreatedByDisplayName(user: { id: number; fullName?: string; username?: string; email?: string }): string {
  return user.fullName || user.username || user.email || String(user.id)
}


export function SalesOrderDetailDrawer({
  contract,
  salesOrder: salesOrderProp,
  open,
  onOpenChange,
  onRenew,
  onReorder,
  onEdit,
  onViewOrders,
  onViewShipments,
}: SalesOrderDetailDrawerProps) {
  const { t, i18n } = useTranslation()
  const { toast } = useToast()
  const isRTL = useIsRTL()
  const isSuperAdmin = useSelector(selectIsSuperAdmin)
  const [activeTab, setActiveTab] = useState('overview')
  const [orders, setOrders] = useState<Order[]>([])
  const [ordersLoading, setOrdersLoading] = useState(false)
  const [shipments, setShipments] = useState<Shipment[]>([])
  const [shipmentsLoading, setShipmentsLoading] = useState(false)
  const [invoices, setInvoices] = useState<Invoice[]>([])
  const [invoicesLoading, setInvoicesLoading] = useState(false)
  const [invoicesLoaded, setInvoicesLoaded] = useState(false)
  const [downloadingPdf, setDownloadingPdf] = useState(false)
  const [cancellingOrderId, setCancellingOrderId] = useState<string | null>(null)
  const [pdfDrawerOpen, setPdfDrawerOpen] = useState(false)
  const [pdfPreview, setPdfPreview] = useState<SalesOrderPdfPreview | null>(null)
  const [pdfError, setPdfError] = useState<string | null>(null)
  const pdfRequestIdRef = useRef(0)

  // Support both salesOrder and deprecated contract prop
  const salesOrder = salesOrderProp || contract

  // Load orders when orders tab or shipments tab is activated (shipments need orders)
  useEffect(() => {
    if ((activeTab === 'orders' || activeTab === 'shipments' || activeTab === 'transactions') && salesOrder && orders.length === 0) {
      setOrdersLoading(true)
      // Use strapiDocumentId for filtering (primary identifier in Strapi v5)
      const salesOrderLookupId = resolveSalesOrderLookupId(salesOrder)
      ordersApi.getOrdersBySalesOrderId(salesOrderLookupId).then((data) => {
        setOrders(data)
        setOrdersLoading(false)
      }).catch(() => {
        setOrdersLoading(false)
      })
    }
  }, [activeTab, salesOrder, orders.length])

  // Load shipments when tab is activated - fetch shipments for all orders under this sales order
  useEffect(() => {
    if (activeTab === 'shipments' && salesOrder && orders.length > 0 && shipments.length === 0) {
      setShipmentsLoading(true)
      // Fetch shipments for all orders under this sales order
      // Order.id already contains documentId (primary identifier)
      Promise.all(orders.map(order => shipmentsApi.getShipmentsByOrderId(order.id)))
        .then((results) => {
          // Flatten the results and remove duplicates by id
          const allShipments = results.flat()
          const uniqueShipments = Array.from(
            new Map(allShipments.map(s => [s.id, s])).values()
          )
          setShipments(uniqueShipments)
          setShipmentsLoading(false)
        })
        .catch(() => {
          setShipmentsLoading(false)
        })
    }
  }, [activeTab, salesOrder, orders, shipments.length])

  useEffect(() => {
    if (activeTab !== 'transactions' || !salesOrder || invoicesLoaded || invoicesLoading) return
    if (orders.length === 0) return

    setInvoicesLoading(true)
    const orderNumbers = orders.map(o => o.orderNumber).filter(Boolean)

    invoiceApi.getInvoicesByOrderNumbers(orderNumbers)
      .then((data) => {
        setInvoices(data)
      })
      .catch(() => {
        setInvoices([])
      })
      .finally(() => {
        setInvoicesLoading(false)
        setInvoicesLoaded(true)
      })
  }, [activeTab, salesOrder, invoicesLoaded, invoicesLoading, orders])

  // Reset state when drawer closes or salesOrder changes
  useEffect(() => {
    if (!open) {
      setActiveTab('overview')
      setOrders([])
      setShipments([])
      setInvoices([])
      setInvoicesLoading(false)
      setInvoicesLoaded(false)
      setPdfDrawerOpen(false)
      setPdfPreviewState(pdfPreview, null, setPdfPreview)
      setPdfError(null)
    }
  }, [open, salesOrder?.id])

  useEffect(() => () => {
    revokePdfUrl(pdfPreview?.url)
  }, [pdfPreview?.url])

  // Memoized values - MUST be before any conditional returns

  if (!salesOrder) return null

  const normalizedStatus = normalizeStatus(salesOrder.status) ?? 'draft'
  const config = resolveStatusConfig(normalizedStatus)
  const StatusIcon = config.icon
  const utilization = calculateUtilization(salesOrder)
  const deliveryLocation = getLocalizedSalesOrderLocation(
    salesOrder.deliveryAddress,
    salesOrder.cityName,
    i18n.language,
  )

  const prepareSalesOrderPdfPreview = async () => {
    if (downloadingPdf) return

    const salesOrderLookupId = resolveSalesOrderLookupId(salesOrder)
    const requestId = pdfRequestIdRef.current + 1
    pdfRequestIdRef.current = requestId

    setPdfDrawerOpen(true)
    setDownloadingPdf(true)
    setPdfError(null)

    try {
      const payload = await salesOrdersApi.getSalesOrderOrdersPdfPayload(salesOrderLookupId, salesOrder.orderNumber)
      if (pdfRequestIdRef.current !== requestId) return

      const preview = createSalesOrderPdfPreview({
        salesOrderId: salesOrderLookupId,
        orderNumber: salesOrder.orderNumber,
        blob: payload.blob,
        filename: payload.filename,
        isNative: isNativePlatform(),
      })
      setPdfPreviewState(pdfPreview, preview, setPdfPreview)
    } catch (error) {
      console.error('[SalesOrderDetailDrawer] Error preparing sales order PDF preview:', error)
      if (pdfRequestIdRef.current === requestId) {
        setPdfError(t('orders.pdfDownloadErrorDesc'))
      }
    } finally {
      if (pdfRequestIdRef.current === requestId) {
        setDownloadingPdf(false)
      }
    }
  }

  const handleDownloadPdf = async (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    e.stopPropagation()
    if (downloadingPdf) return

    if (shouldUsePdfPreview(isNativePlatform())) {
      await prepareSalesOrderPdfPreview()
      return
    }

    setDownloadingPdf(true)
    try {
      const salesOrderLookupId = resolveSalesOrderLookupId(salesOrder)
      await salesOrdersApi.downloadSalesOrderOrdersPdf(salesOrderLookupId, salesOrder.orderNumber)
    } finally {
      setDownloadingPdf(false)
    }
  }

  const handlePdfShare = () => {
    void shareSalesOrderPdfPreview({ preview: pdfPreview, t, toast })
  }

  const handlePdfRetry = () => {
    void prepareSalesOrderPdfPreview()
  }

  const handlePdfDrawerOpenChange = (nextOpen: boolean) => {
    setPdfDrawerOpen(nextOpen)
    if (!nextOpen) {
      setPdfPreviewState(pdfPreview, null, setPdfPreview)
      setPdfError(null)
      setDownloadingPdf(false)
    }
  }

  const handleCancelOrder = async (order: Order, e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    e.stopPropagation()
    if (cancellingOrderId) return
    if (!canCancelLinkedOrder(order)) return

    setCancellingOrderId(order.id)
    try {
      await ordersApi.cancelOrder(order.id, t('orders.cancelReasonUserRequested'))
      setOrders((prev) => prev.map((item) => (
        item.id === order.id ? { ...item, status: 'cancelled' } : item
      )))
      toast({
        title: t('orders.cancelSuccess'),
        description: t('orders.cancelSuccessDesc', { orderNumber: order.orderNumber }),
      })
    } catch {
      toast({
        title: t('orders.cancelError'),
        description: t('orders.cancelErrorDesc', { orderNumber: order.orderNumber }),
        variant: 'destructive',
      })
    } finally {
      setCancellingOrderId(null)
    }
  }

  // Header content for BaseDrawer
  const headerContent = (
    <div className="flex items-center gap-3">
      <div className="w-12 h-12 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
        <FileText className="w-6 h-6 text-white" />
      </div>
      <div>
        <Badge className="bg-white/20 text-white border-0 backdrop-blur-sm mb-1">
          <StatusIcon className="w-3 h-3 me-1" />
          {t(`salesOrders.status.${normalizedStatus}`)}
        </Badge>
        <h2 className="text-lg font-bold text-white">{isRTL ? salesOrder.name.ar : salesOrder.name.en}</h2>
        <p className="text-white/70 text-sm">{salesOrder.orderNumber}</p>
      </div>
    </div>
  )

  // Footer content
  const footerContent = (
    <div className="flex flex-col gap-3">
      {/* Primary Action Row */}
      <div className="flex gap-2">
        {onReorder && (
          <Button
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); onReorder(salesOrder); }}
            className="flex-1 h-11 rounded-xl bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 hover:shadow-xl hover:shadow-emerald-500/30 hover:-translate-y-0.5 transition-all duration-200 text-white shadow-lg shadow-emerald-500/25 font-semibold gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            {t('salesOrders.reorder')}
          </Button>
        )}
      </div>
      {/* Secondary Actions Row */}
      <div className="flex gap-2">
        <Button
          onClick={handleDownloadPdf}
          variant="outline"
          className="flex-1 h-10 rounded-xl border-border hover:bg-muted/80 hover:border-primary/30 hover:text-primary hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 font-medium gap-2"
          disabled={downloadingPdf}
        >
          {downloadingPdf ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
          {t('salesOrders.downloadPDF')}
        </Button>
        {onEdit && (
          <Button
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); onEdit(); }}
            variant="outline"
            className="flex-1 h-10 rounded-xl border-border hover:bg-muted/80 hover:border-primary/30 hover:text-primary hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 font-medium gap-2"
          >
            <Edit3 className="w-4 h-4" />
            {t('common.edit')}
          </Button>
        )}
        <Button
          variant="ghost"
          className="flex-1 h-10 rounded-xl text-muted-foreground hover:text-foreground hover:bg-muted/80 hover:shadow-sm hover:-translate-y-0.5 transition-all duration-200 font-medium"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); onOpenChange(false); }}
        >
          {t('common.close')}
        </Button>
      </div>
    </div>
  )

  // Tabs content for stickyContent
  const tabsContent = (
    <div className="px-6 py-3 bg-slate-50/50 dark:bg-slate-800/30 border-b border-border/20">
      <div className="glass-card rounded-2xl overflow-hidden border border-border/50 flex">
        <button
          onClick={() => setActiveTab('overview')}
          className={cn(
            "flex-1 px-4 py-4 gap-2 text-sm font-medium inline-flex items-center justify-center relative transition-[background-color,color]",
            activeTab === 'overview' 
              ? "bg-primary/15 text-primary font-semibold" 
              : "text-muted-foreground hover:text-foreground hover:bg-secondary/50"
          )}
        >
          <Layers className={cn("w-4 h-4", activeTab === 'overview' && "text-primary")} />
          {t('common.overview')}
          {activeTab === 'overview' && (
            <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary rounded-t-full" />
          )}
        </button>
        <button
          onClick={() => setActiveTab('orders')}
          className={cn(
            "flex-1 px-4 py-4 gap-2 text-sm font-medium inline-flex items-center justify-center relative transition-[background-color,color]",
            activeTab === 'orders' 
              ? "bg-primary/15 text-primary font-semibold" 
              : "text-muted-foreground hover:text-foreground hover:bg-secondary/50"
          )}
        >
          <ShoppingCart className={cn("w-4 h-4", activeTab === 'orders' && "text-primary")} />
          {t('contracts.orders')}
          {activeTab === 'orders' && (
            <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary rounded-t-full" />
          )}
        </button>
        <button
          onClick={() => setActiveTab('shipments')}
          className={cn(
            "flex-1 px-4 py-4 gap-2 text-sm font-medium inline-flex items-center justify-center relative transition-[background-color,color]",
            activeTab === 'shipments' 
              ? "bg-primary/15 text-primary font-semibold" 
              : "text-muted-foreground hover:text-foreground hover:bg-secondary/50"
          )}
        >
          <Truck className={cn("w-4 h-4", activeTab === 'shipments' && "text-primary")} />
          {t('salesOrders.shipments')}
          {activeTab === 'shipments' && (
            <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary rounded-t-full" />
          )}
        </button>
        <button
          onClick={() => setActiveTab('transactions')}
          className={cn(
            "flex-1 px-4 py-4 gap-2 text-sm font-medium inline-flex items-center justify-center relative transition-[background-color,color]",
            activeTab === 'transactions'
              ? "bg-primary/15 text-primary font-semibold"
              : "text-muted-foreground hover:text-foreground hover:bg-secondary/50"
          )}
        >
          <Receipt className={cn("w-4 h-4", activeTab === 'transactions' && "text-primary")} />
          {t('common.transactions')}
          {activeTab === 'transactions' && (
            <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary rounded-t-full" />
          )}
        </button>
      </div>
    </div>
  )

  return (
    <>
    <BaseDrawer
      open={open}
      onOpenChange={onOpenChange}
      isRTL={isRTL}
      headerGradient={config.gradient}
      headerContent={headerContent}
      stickyContent={tabsContent}
      footerContent={footerContent}
      fixedWidth={600}
      title={t('salesOrders.salesOrderDetails')}
      data-testid="sales-orders-detail-drawer"
    >
      {/* Tab Content - scrollable */}
      {activeTab === 'overview' && (
              <>
                {/* Consumption Card */}
                <div className="p-5 rounded-2xl bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700">
                  {/* Consumption Progress Bar */}
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm font-semibold text-muted-foreground">{t('salesOrders.consumption')}</span>
                      <div className={cn(
                        "px-3 py-1 rounded-lg text-sm font-bold",
                        utilization >= 90
                          ? "bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-400"
                          : utilization >= 70
                            ? "bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-400"
                            : "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400"
                      )}>
                        {utilization}%
                      </div>
                    </div>

                    {/* Visual Progress Bar */}
                    <div className="relative">
                      <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden shadow-inner">
                        <div
                          className={cn(
                            "h-full rounded-full transition-[width] duration-700 ease-out relative",
                            utilization >= 90
                              ? "bg-gradient-to-r from-red-500 to-red-400"
                              : utilization >= 70
                                ? "bg-gradient-to-r from-amber-500 to-amber-400"
                                : "bg-gradient-to-r from-emerald-500 to-emerald-400"
                          )}
                          style={{ width: `${Math.min(utilization, 100)}%` }}
                        >
                          <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-white/20 rounded-full" />
                        </div>
                      </div>
                    </div>

                  </div>
                </div>

                {/* Duration - Horizontal */}
                <div>
                  <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                    <CalendarDays className="w-4 h-4 text-muted-foreground" />
                    {t('contracts.duration')}
                  </h3>
                  <div className="p-3 rounded-xl bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800">
                    <p className="text-xs text-emerald-600 dark:text-emerald-400 font-medium mb-0.5">{t('contracts.startDate')}</p>
                    <p className="font-semibold text-foreground text-sm">{salesOrder.startDate}</p>
                  </div>
                </div>

                {/* Location/Delivery Address */}
                <div>
                  <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-muted-foreground" />
                    {t('salesOrders.deliveryAddress')}
                  </h3>
                  <div className="p-4 rounded-xl bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center flex-shrink-0">
                        <MapPin className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        {deliveryLocation ? (
                          <p className="text-sm font-medium text-foreground leading-relaxed">
                            {deliveryLocation}
                          </p>
                        ) : salesOrder.deliveryAddressId ? (
                          <p className="text-sm text-muted-foreground">
                            {t('addressbook.addressId')}: {salesOrder.deliveryAddressId}
                          </p>
                        ) : (
                          <p className="text-sm text-muted-foreground">
                            {t('common.notAvailable')}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Order Lines */}
                {salesOrder.lines && salesOrder.lines.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                      <Package className="w-4 h-4 text-primary" />
                      {t('salesOrders.orderLines')} ({salesOrder.lines.length})
                    </h4>
                    <div className="space-y-2">
                      {salesOrder.lines.map((line, index) => {
                        const lineUtil = line.allocatedQuantity.value > 0
                          ? Math.round((line.usedQuantity.value / line.allocatedQuantity.value) * 100)
                          : 0
                        const isLow = lineUtil >= 80

                        return (
                          <div
                            key={index}
                            className={cn(
                              "p-3 rounded-xl border transition-shadow hover:shadow-sm",
                              isLow
                                ? "bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800"
                                : "bg-slate-50 dark:bg-slate-800/50 border-slate-200 dark:border-slate-700"
                            )}
                          >
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <Package className={cn("w-4 h-4", isLow ? "text-amber-600" : "text-primary")} />
                                <span className="font-medium text-foreground text-sm truncate">
                                  {(i18n.language.startsWith('en')
                                    ? getLocalizedValue(line.product, i18n.language) || line.variantName
                                    : getLocalizedValue(line.product, i18n.language) || line.variantName) || t('salesOrders.product')}
                                </span>
                              </div>
                              <span className={cn("text-sm font-bold", isLow ? "text-amber-600" : "text-primary")}>
                                {lineUtil}%
                              </span>
                            </div>

                            <div className="h-1.5 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                              <div
                                className={cn(
                                  "h-full rounded-full transition-[width]",
                                  isLow ? "bg-amber-500" : "bg-primary"
                                )}
                                style={{ width: `${lineUtil}%` }}
                              />
                            </div>

                            <div className="flex justify-between text-xs text-muted-foreground mt-1.5">
                              <span>
                                {line.usedQuantity.value.toLocaleString()} / {line.allocatedQuantity.value.toLocaleString()}{' '}
                                {getLocalizedSalesOrderUnit(line.allocatedQuantity.unit, t, i18n.language)}
                              </span>
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                )}

                {/* Notes */}
                {salesOrder.notes && (
                  <div className="p-3 rounded-xl bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700">
                    <p className="text-xs text-muted-foreground font-semibold uppercase tracking-wide mb-1">{t('contracts.notes')}</p>
                    <p className="text-sm text-foreground">{salesOrder.notes}</p>
                  </div>
                )}

                {/* Created By — visible to super admins only */}
                {isSuperAdmin && salesOrder.createdByUser && (
                  <div className="p-3 rounded-xl bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700">
                    <p className="text-xs text-muted-foreground font-semibold uppercase tracking-wide mb-1">{t('contracts.createdBy')}</p>
                    <p className="text-sm text-foreground">{getCreatedByDisplayName(salesOrder.createdByUser)}</p>
                  </div>
                )}
              </>
            )}

            {activeTab === 'orders' && (
              <>
                {ordersLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-primary" />
                  </div>
                ) : orders.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <div className="w-16 h-16 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center mb-4">
                      <ShoppingCart className="w-8 h-8 text-slate-400" />
                    </div>
                    <p className="text-sm font-medium text-foreground mb-1">{t('orders.noOrders')}</p>
                    <p className="text-xs text-muted-foreground">{t('salesOrders.noOrdersForContract')}</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {orders.map((order) => (
                      <div key={order.id} className="space-y-2">
                        <OrderCard
                          order={order}
                          onClick={onViewOrders ? () => onViewOrders(order.id) : undefined}
                        />
                        {canCancelLinkedOrder(order) && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="w-full h-9 rounded-lg border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700 dark:border-red-800/60 dark:text-red-400 dark:hover:bg-red-900/20"
                            onClick={(event) => handleCancelOrder(order, event)}
                            disabled={cancellingOrderId === order.id}
                          >
                            {cancellingOrderId === order.id ? (
                              <Loader2 className="w-4 h-4 animate-spin me-2" />
                            ) : (
                              <Ban className="w-4 h-4 me-2" />
                            )}
                            {t('orders.cancelOrder')}
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}

            {activeTab === 'shipments' && (
              <>
                {shipmentsLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-primary" />
                  </div>
                ) : shipments.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <div className="w-16 h-16 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center mb-4">
                      <Truck className="w-8 h-8 text-slate-400" />
                    </div>
                    <p className="text-sm font-medium text-foreground mb-1">{t('shipments.noShipments')}</p>
                    <p className="text-xs text-muted-foreground">{t('salesOrders.noShipmentsForContract')}</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {shipments.map((shipment) => (
                      <ShipmentCard
                        key={shipment.id}
                        shipment={shipment}
                        onClick={onViewShipments ? () => onViewShipments(shipment.id) : undefined}
                      />
                    ))}
                  </div>
                )}
              </>
            )}

            {activeTab === 'transactions' && (
              <>
                {invoicesLoading || ordersLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-primary" />
                  </div>
                ) : invoices.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <div className="w-16 h-16 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center mb-4">
                      <Receipt className="w-8 h-8 text-slate-400" />
                    </div>
                    <p className="text-sm font-medium text-foreground mb-1">{t('finance.noInvoices', 'No Invoices')}</p>
                    <p className="text-xs text-muted-foreground">{t('finance.noInvoicesDescription', 'No invoices found for this sales order')}</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {invoices.map((invoice) => (
                      <div key={invoice.id} className="glass-card p-4 rounded-xl">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <Receipt className="w-4 h-4 text-primary" />
                            <span className="text-sm font-semibold text-foreground">{invoice.invoiceNumber}</span>
                          </div>
                          <Badge variant="outline" className="text-xs">
                            {invoice.status}
                          </Badge>
                        </div>
                        <div className="flex items-center justify-between text-xs text-muted-foreground">
                          <span>{invoice.issueDate}</span>
                          <span className="font-semibold text-foreground">
                            {invoice.total.currency} {invoice.total.amount.toLocaleString()}
                          </span>
                        </div>
                        {invoice.documentReference && (
                          <div className="mt-1 text-xs text-muted-foreground">
                            {t('orders.orderNumber', 'Order')}: {invoice.documentReference}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
    </BaseDrawer>
    <OrderPdfDetailDrawer
      open={pdfDrawerOpen}
      onOpenChange={handlePdfDrawerOpenChange}
      orderNumber={salesOrder.orderNumber}
      fileName={pdfPreview?.fileName}
      fileSize={pdfPreview?.fileSize}
      pdfUrl={pdfPreview?.url}
      pdfBlob={pdfPreview?.blob}
      previewMode={getPdfPreviewMode(isNativePlatform())}
      isLoading={downloadingPdf}
      error={pdfError}
      onShare={handlePdfShare}
      onRetry={handlePdfRetry}
    />
    </>
  )
}

// Legacy export for backwards compatibility
export { SalesOrderDetailDrawer as ContractDetailDrawer }
